import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { mutate } from "swr";
import { IconCheck } from "@tabler/icons-react";
import { Heading } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { SimpleTable } from "../../../../components/common/simple-table";
import { Pagination } from "../../../../components/common/pagination";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useAdminUserProductRequests } from "../../hooks/useAdminUserProductRequests";
import { useProductRequestsGroupedByUser } from "../../hooks/useProductRequestsGroupedByUser";
import { usePaginationSortingUrlSync } from "../../../pagination/hooks/usePaginationSortingUrlSync";
import { adminProductRequestsService } from "../../../api_client";
import { buildUserProductRequestsColumns } from "../components/user-product-requests-columns";
import { useCategoryLabels } from "../../../products/hooks/useCategoryLabels";
import { BulkApproveDialog } from "../components/bulk-approve-dialog";
import { showSuccessToast, showErrorToast } from "../../../../utils/toasts";
import type { BulkApproveProductRequestsDto, ProductRequestQueryDto } from "../../../../api";
import { ProductRequestDtoStatusEnum } from "../../../../api";

export function UserProductRequestsReviewPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);

  const { pagination } = usePaginationSortingUrlSync({ defaultPageSize: 10 });

  const { data: requestsData, isLoading: requestsLoading } = useAdminUserProductRequests({
    userId,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  });

  // Fetch user name for the breadcrumb — grouped endpoint with userId filter
  const { data: userGroupData } = useProductRequestsGroupedByUser(
    { userId } as ProductRequestQueryDto,
  );

  const userName = userGroupData?.data?.[0]?.userName ?? "User";

  const rows = requestsData?.data ?? [];
  const pageCount = requestsData?.meta?.pageCount ?? 0;

  const hasPending = rows.some((r) => r.status === ProductRequestDtoStatusEnum.Pending);

  const goToDetail = (id: string) => {
    navigate(AppRoutes.ADMIN_PRODUCT_REQUEST_REVIEW.replace(":id", id), {
      state: { userId, userName },
    });
  };

  const categoryLabel = useCategoryLabels();
  const columns = buildUserProductRequestsColumns(goToDetail, categoryLabel);

  const handleBulkApprove = async (marginPercentage: number, roundUp: boolean) => {
    const dto: BulkApproveProductRequestsDto = { marginPercentage, roundUp };
    try {
      const { data: result } = await adminProductRequestsService.bulkApproveProductRequests(dto);
      showSuccessToast(
        `Bulk approval done: ${result.approved} approved, ${result.failed} failed of ${result.total}`,
      );
      mutate((key) => typeof key === "string" && key.startsWith("admin-"));
    } catch (err: unknown) {
      showErrorToast(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Bulk approval failed",
      );
      throw err;
    }
  };

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghostAccent"
            size="sm"
            onClick={() => navigate(AppRoutes.ADMIN_PRODUCT_REQUESTS)}
            className="!h-auto !px-0 !py-0"
          >
            <Heading variant="h3" tone="inherit">← Product review</Heading>
          </Button>
          <Heading variant="h3" tone="muted">/</Heading>
          <Heading variant="h3" tone="dark">{userName}</Heading>
        </div>
        {hasPending && (
          <Button
            variant="filledDark"
            size="sm"
            leftIcon={<IconCheck size={16} />}
            onClick={() => setBulkApproveOpen(true)}
            className="!px-5"
          >
            Bulk Approve
          </Button>
        )}
      </div>

      {requestsLoading ? (
        <ShimmerLoader message="Loading product requests…" lines={6} />
      ) : (
        <SimpleTable
          columns={columns}
          data={rows}
          keyExtractor={(r) => r.id}
          onRowClick={(r) => goToDetail(r.id)}
          footer={pageCount > 1 ? <PaginationFooter pageCount={pageCount} /> : undefined}
        />
      )}

      <BulkApproveDialog
        open={bulkApproveOpen}
        setOpen={setBulkApproveOpen}
        onApprove={handleBulkApprove}
      />
    </div>
  );
}

function PaginationFooter({ pageCount }: { pageCount: number }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") ?? "1") || 1;
  return (
    <div className="flex justify-center">
      <Pagination
        page={page}
        pageCount={pageCount}
        onPageChange={(next) => {
          const params = new URLSearchParams(searchParams);
          params.set("page", String(next));
          setSearchParams(params, { replace: true });
        }}
      />
    </div>
  );
}
